/** @jsxImportSource @emotion/react */
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { UserContext } from "../../context/UserContext";
import { useCookies } from "react-cookie";
import SignInTemplate from "../../templates/SignIn";
import TextBox from "../../components/TextBox";
import Button from "../../components/Button";
import { useGetSignInFormStyles } from "./index.styles";
import googleIcon from "../../assets/images/svg/googleIcon.svg";
import { baseUrl, REACT_APP_GOOGLE_CLIENT_ID } from "../../connections";
import { requestGoogleProfile } from "../../utils/googleAuth";
import {
  getPostAuthRoute,
  normalizeOAuthProfile,
  persistUserSession,
} from "../../utils/authSession";

const ERROR_MESSAGES = {
  cancelled: "Google sign in was cancelled. Please try again.",
  missing_client_id:
    "Google sign in is not configured. Add REACT_APP_GOOGLE_CLIENT_ID for this frontend.",
  popup_failed: "Google sign in could not open. Please try again.",
  profile_failed: "Google profile could not be loaded. Please try again.",
  script_failed:
    "Google sign in could not be loaded right now. Please try again.",
  access_denied: "Google sign in was denied. Please try again.",
  state_invalid: "Your sign in session expired. Please try again.",
  invalid_grant:
    "Google sign in could not be completed right now. Please try again.",
  invalid_client:
    "Google sign in is not configured correctly. Please contact support.",
  invalid_request: "Google sign in request is invalid. Please try again.",
  exchange_failed:
    "Google sign in could not be completed right now. Please try again.",
};

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const OTP_LENGTH = 6;
const ALLOWED_RETURN_HOSTS = new Set([
  "stage-bingo.money-mails.com",
  "bingo.money-mails.com",
  "localhost",
]);

const getSafeReturnUrl = (search) => {
  const params = new URLSearchParams(search || "");
  const candidate = params.get("return_to") || params.get("returnTo") || "";

  if (!candidate) {
    return "";
  }

  try {
    const parsed = new URL(candidate);
    if (!ALLOWED_RETURN_HOSTS.has(parsed.hostname)) {
      return "";
    }
    return parsed.toString();
  } catch (_error) {
    return "";
  }
};

const parseApiResponse = async (response) => {
  const responseText = await response.text();
  let payload = null;

  try {
    payload = responseText ? JSON.parse(responseText) : null;
  } catch (_error) {
    payload = null;
  }

  if (!response.ok) {
    throw new Error(
      payload?.message ||
        payload?.error ||
        responseText ||
        "Request could not be completed.",
    );
  }

  return payload || {};
};

const Login = () => {
  const classes = useGetSignInFormStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const { setUserDetails } = useContext(UserContext);
  const otpInputRefs = useRef([]);
  const [, setCookie] = useCookies([
    "dInfoUserEmail",
    "dInfoUserId",
    "dInfoUserType",
  ]);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [authError, setAuthError] = useState("");
  const [otpValues, setOtpValues] = useState(Array(OTP_LENGTH).fill(""));
  const [otpError, setOtpError] = useState("");
  const [otpStatus, setOtpStatus] = useState("");
  const [loginStep, setLoginStep] = useState("entry");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const returnTo = useMemo(
    () => getSafeReturnUrl(location.search),
    [location.search],
  );
  const signupRoute = returnTo
    ? `/signup?return_to=${encodeURIComponent(returnTo)}`
    : "/signup";

  useEffect(() => {
    if (resendCooldown <= 0) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setResendCooldown((current) => Math.max(current - 1, 0));
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

  const focusOtpInput = (index) => {
    window.setTimeout(() => {
      otpInputRefs.current[index]?.focus();
      otpInputRefs.current[index]?.select?.();
    }, 0);
  };

  const resetOtpState = () => {
    setOtpValues(Array(OTP_LENGTH).fill(""));
    setOtpError("");
  };

  const handleAuthSuccess = (authData) => {
    persistUserSession({
      data: authData,
      setUserDetails,
      setCookie,
    });

    if (returnTo) {
      window.location.assign(returnTo);
      return;
    }

    navigate(getPostAuthRoute(authData), { replace: true });
  };

  const handleGoogleAuthSuccess = (authData) => {
    persistUserSession({
      data: authData,
      setUserDetails,
      setCookie,
    });

    if (returnTo) {
      window.location.assign(returnTo);
      return;
    }

    navigate(getPostAuthRoute(authData), { replace: true });
  };

  const sendEmailOtp = async ({ isResend = false } = {}) => {
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      setEmailError("Enter your email address.");
      return;
    }

    if (!EMAIL_REGEX.test(normalizedEmail)) {
      setEmailError("Enter a valid email address.");
      return;
    }

    setEmailError("");
    setAuthError("");
    setOtpError("");
    setOtpStatus("");
    setIsSendingOtp(true);

    try {
      const response = await fetch(`${baseUrl}/user/email/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: normalizedEmail,
        }),
      });
      const result = await parseApiResponse(response);

      setEmail(normalizedEmail);
      setLoginStep("verify");
      resetOtpState();
      setOtpStatus(
        result?.message || `Verification code sent to ${normalizedEmail}.`,
      );
      setResendCooldown(Number(result?.data?.retryAfter) || 30);
      focusOtpInput(0);
    } catch (error) {
      if (isResend) {
        setOtpError(error?.message || "Code could not be resent.");
      } else {
        setAuthError(error?.message || "Code could not be sent.");
      }
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleEmailContinue = async (event) => {
    event.preventDefault();
    await sendEmailOtp();
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    const otp = otpValues.join("");

    if (otp.length !== OTP_LENGTH) {
      setOtpError("Enter the 6-digit code sent to your email.");
      return;
    }

    setIsVerifyingOtp(true);
    setOtpError("");
    setAuthError("");

    try {
      const response = await fetch(`${baseUrl}/user/email/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          otp,
        }),
      });
      const result = await parseApiResponse(response);
      const authData = {
        ...(result?.data || {}),
        ...(result?.bingoDetail || {}),
        token: result?.token || result?.data?.token || "",
      };

      if (!authData?.token) {
        navigate("/home", { replace: true });
        return;
      }

      handleAuthSuccess(authData);
    } catch (error) {
      setOtpError(error?.message || "OTP verification failed.");
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleOtpInputChange = (event, index) => {
    const nextValue = String(event.target.value || "")
      .replace(/\D/g, "")
      .slice(-1);

    setOtpValues((current) => {
      const next = [...current];
      next[index] = nextValue;
      return next;
    });
    setOtpError("");

    if (nextValue && index < OTP_LENGTH - 1) {
      focusOtpInput(index + 1);
    }
  };

  const handleOtpKeyDown = (event, index) => {
    if (event.key === "Backspace" && !otpValues[index] && index > 0) {
      focusOtpInput(index - 1);
    }
  };

  const handleOtpPaste = (event) => {
    event.preventDefault();
    const pastedValue = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);

    if (!pastedValue) {
      return;
    }

    const next = Array(OTP_LENGTH).fill("");
    pastedValue.split("").forEach((value, index) => {
      next[index] = value;
    });
    setOtpValues(next);
    setOtpError("");

    const focusIndex = Math.min(pastedValue.length, OTP_LENGTH - 1);
    focusOtpInput(focusIndex);
  };

  const handleGoogleLogin = async () => {
    setAuthError("");
    setIsGoogleLoading(true);

    try {
      const profile = normalizeOAuthProfile(
        await requestGoogleProfile({
          clientId: REACT_APP_GOOGLE_CLIENT_ID,
        }),
      );

      const response = await fetch(`${baseUrl}/auth/google/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...profile,
          accessToken: profile.accessToken || profile.access_token || "",
        }),
      });
      const result = await parseApiResponse(response);

      if (!result?.data) {
        throw new Error(result?.message || "Google sign in failed.");
      }

      handleGoogleAuthSuccess(result.data);
    } catch (error) {
      setAuthError(
        ERROR_MESSAGES[error?.code] ||
          error?.message ||
          "Google sign in failed. Please try again.",
      );
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <SignInTemplate>
      <div css={classes.container}>
        <div css={classes.card}>
          <h2 css={classes.heading}>Login to your Account</h2>

          {loginStep === "entry" ? (
            <>
              <Button
                type="button"
                variant="contained"
                css={classes.googleButton}
                startIcon={
                  <img src={googleIcon} alt="" css={classes.providerIcon} />
                }
                onClick={handleGoogleLogin}
                disabled={isGoogleLoading || isSendingOtp}
              >
                {isGoogleLoading
                  ? "Signing in with Google..."
                  : "Sign in with Google"}
              </Button>

              <div css={classes.divider}>
                <span css={classes.dividerText}>or continue with email</span>
              </div>

              <form css={classes.formContainer} onSubmit={handleEmailContinue}>
                <TextBox
                  color="#FFFFFF"
                  label="Email Address"
                  type="email"
                  value={email}
                  fullWidth
                  onChange={(_event, value) => {
                    setEmail(value);
                    if (emailError) {
                      setEmailError("");
                    }
                    if (authError) {
                      setAuthError("");
                    }
                  }}
                />
                {emailError && <div css={classes.errorBox}>{emailError}</div>}
                {authError && <div css={classes.errorBox}>{authError}</div>}

                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  fullWidth
                  css={classes.submitButton}
                  disabled={isSendingOtp || isGoogleLoading}
                >
                  {isSendingOtp ? "Sending code..." : "Continue"}
                </Button>
              </form>
            </>
          ) : (
            <div css={classes.verificationPanel}>
              <div css={classes.verificationHeader}>
                <div css={classes.emailPill}>{email}</div>
                <button
                  type="button"
                  css={classes.changeEmailButton}
                  onClick={() => {
                    setLoginStep("entry");
                    setOtpError("");
                    setOtpStatus("");
                    setAuthError("");
                  }}
                >
                  Change
                </button>
              </div>

              <form css={classes.formContainer} onSubmit={handleVerifyOtp}>
                <div css={classes.verificationCopy}>
                  <h3 css={classes.verificationTitle}>
                    Check your inbox and enter code
                  </h3>
                </div>
                <div css={classes.otpFieldsWrapper} onPaste={handleOtpPaste}>
                  {otpValues.map((value, index) => (
                    <input
                      key={`login-otp-${index}`}
                      ref={(element) => {
                        otpInputRefs.current[index] = element;
                      }}
                      type="text"
                      inputMode="numeric"
                      autoComplete={index === 0 ? "one-time-code" : "off"}
                      maxLength={1}
                      value={value}
                      css={classes.otpField}
                      onChange={(event) => handleOtpInputChange(event, index)}
                      onKeyDown={(event) => handleOtpKeyDown(event, index)}
                    />
                  ))}
                </div>
                {otpStatus && <div css={classes.successBox}>{otpStatus}</div>}
                {otpError && <div css={classes.errorBox}>{otpError}</div>}
                {authError && <div css={classes.errorBox}>{authError}</div>}

                <div css={classes.verificationActions}>
                  <button
                    type="button"
                    css={classes.resendLink}
                    onClick={() => sendEmailOtp({ isResend: true })}
                    disabled={resendCooldown > 0 || isSendingOtp}
                  >
                    {resendCooldown > 0
                      ? `Resend in ${resendCooldown}s`
                      : isSendingOtp
                        ? "Sending..."
                        : "Resend code"}
                  </button>
                </div>

                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  fullWidth
                  css={classes.submitButton}
                  disabled={isVerifyingOtp}
                >
                  {isVerifyingOtp ? "Verifying..." : "Verify and Login"}
                </Button>
              </form>
            </div>
          )}

          <p css={classes.termsCopy}>
            Continue with Google or sign in with your email using a one-time
            verification code. By continuing, you agree to the{" "}
            <Link to="/terms" css={classes.inlineLink}>
              Terms and Conditions
            </Link>{" "}
            and{" "}
            <Link to="/privacy" css={classes.inlineLink}>
              Privacy Policy
            </Link>
            .
          </p>

          <Button
            type="button"
            variant="text"
            color="secondary"
            css={classes.signupButton}
            onClick={() => navigate(signupRoute)}
          >
            Don&apos;t have an account? Sign up
          </Button>
        </div>
      </div>
    </SignInTemplate>
  );
};

export default Login;
